import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";

export default function AboutPage() {
  const [formState, setFormState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>("input[name='phone_alt']");
    if (honeypotEl && honeypotEl.value.trim() !== "") {
      setFormState("success");
      form.reset();
      return;
    }

    setFormState("loading");
    setFormError("");

    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9on88qftmtnfcfh0abg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === "OK") {
        setFormState("success");
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.["meta"] as Record<string, unknown> | undefined;
        const detail = serverMsg?.["message"] || serverMsg?.["detail"] || responseText;
        setFormError(typeof detail === "string" ? detail : "提交失败，请稍后再试");
        setFormState("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormState("error");
    }
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于迷妹漫画",
    description: "迷妹漫画是专业的漫画全集在线阅读平台，提供完结漫画大全，支持竖屏连续滚动阅读体验。",
    url: "https://mimeimanga.com/about",
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "迷妹漫画",
    url: "https://mimeimanga.com/",
    description: "迷妹漫画是专业的漫画全集在线阅读平台，提供完结漫画大全，支持竖屏连续滚动阅读体验。",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "contact@mimeimanga.com",
      availableLanguage: ["Chinese"],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <div className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <Breadcrumb items={[
          { label: "首页", href: "/" },
          { label: "关于我们" },
        ]} />

        <main>
          {/* Brand intro */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">关于迷妹漫画</h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                迷妹漫画成立于2023年，是一家专注于漫画全集、完结漫画与漫画完整版资源的在线阅读平台。
                我们致力于为广大漫画爱好者提供最优质、最全面的漫画阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14 max-w-4xl mx-auto">
              {[
                { icon: "ri-eye-line", title: "我们的愿景", desc: "成为华语世界最受欢迎的漫画在线阅读平台，让每一位漫画迷都能在这里找到自己喜欢的作品，享受沉浸式的阅读体验。" },
                { icon: "ri-heart-pulse-line", title: "我们的使命", desc: "聚合海量优质漫画资源，提供免费、便捷、流畅的在线阅读服务，让完结漫画大全触手可及，让漫画迷一次性补完大结局。" },
                { icon: "ri-star-line", title: "核心价值", desc: "坚持免费阅读理念，持续更新漫画资源，不断优化阅读体验。漫画全集、完结漫画、漫画完整版，一网打尽。" },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-14">
              {[
                { num: "5000+", label: "漫画资源" },
                { num: "100万+", label: "月活用户" },
                { num: "99.7%", label: "用户好评率" },
                { num: "24h", label: "更新速度" },
              ].map((stat, i) => (
                <div key={i} className="text-center p-5 rounded-lg bg-background-100">
                  <div className="text-xl md:text-2xl font-heading font-bold text-primary-600 mb-1">{stat.num}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 text-center mb-8">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
                {[
                  { name: "小橘子", comment: "终于找到一个漫画全集这么全的网站了！完结漫画大全直接看到大结局，太爽了！下拉式阅读体验也超级棒。", rating: 5 },
                  { name: "漫迷小K", comment: "每天都要来迷妹漫画追更，更新速度是真的快。漫画完整版资源丰富，各种分类都有，强烈推荐！", rating: 5 },
                  { name: "追漫少女", comment: "免费漫画网站里做得最好的一个，页面干净没有乱七八糟的广告，阅读体验满分。收藏了无数完结漫画！", rating: 5 },
                  { name: "夜行读者", comment: "迷妹漫画的漫画全集收录得太全了，好多找了很久的完结漫画在这里都找到了，简直是漫画迷的天堂。", rating: 5 },
                  { name: "咖啡不加糖", comment: "竖屏连续滚动阅读真的很舒服，不用翻页，一路往下滑就能看完一整话。完结漫画大全帮我补完了好多大结局。", rating: 5 },
                  { name: "二次元宅", comment: "从朋友那里知道的迷妹漫画，用了一次就离不开了。漫画完整版更新及时，画质也很好，值得所有漫画迷拥有。", rating: 5 },
                ].map((review, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }, (_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.comment}</p>
                    <p className="text-xs text-foreground-400">— {review.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 text-center mb-8">联系我们</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-800">contact@mimeimanga.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-customer-service-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">客服微信</p>
                        <p className="text-sm text-foreground-800">mimei_comic</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-0.5">工作时间</p>
                        <p className="text-sm text-foreground-800">周一至周日 9:00 - 22:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的昵称"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 mb-3 focus:outline-none focus:border-primary-400"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 mb-3 focus:outline-none focus:border-primary-400"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言..."
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 mb-3 focus:outline-none focus:border-primary-400 resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="contact-form-honeypot"
                    />
                    {formState === "success" && (
                      <div className="mb-3 text-xs text-accent-600 bg-accent-50 px-3 py-2 rounded-lg">消息已发送成功！我们会尽快回复您。</div>
                    )}
                    {formState === "error" && (
                      <div className="mb-3 text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-lg">{formError}</div>
                    )}
                    <button
                      type="submit"
                      disabled={formState === "loading"}
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {formState === "loading" ? "发送中..." : "发送消息"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}